import PageHeader from "@/components/PageHeader";
import ProfileForm from "@/components/ProfileForm";
import AiAssistant from "@/components/AiAssistant";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Compass, Sparkles, Loader2, Briefcase, GraduationCap, Target, TrendingUp, Route, ExternalLink, Lightbulb, Building2, MapPin, User, Heart } from "lucide-react";

const GROUPS = [
  {
    title: "Yrkesmässig riktning",
    fields: [
      { name: "job_types", label: "Vilka typer av jobb söker du?", type: "tags", full: true, placeholder: "Skriv och tryck Enter" },
      { name: "industries", label: "Branscher du är intresserad av", type: "tags", full: true, placeholder: "Skriv och tryck Enter" },
      { name: "roles", label: "Önskade roller", type: "tags", full: true, placeholder: "Skriv och tryck Enter" },
      { name: "future_goals", label: "Framtidsmål", type: "textarea", full: true },
      { name: "study_goals", label: "Studiemål", type: "textarea", full: true },
    ],
  },
  {
    title: "Praktiska förutsättningar",
    fields: [
      { name: "environment", label: "Arbetsmiljöpreferenser", type: "text" },
      { name: "location", label: "Geografiska önskemål", type: "text" },
      { name: "hours", label: "Arbetstider", type: "select", options: ["Dagtid", "Kväll", "Natt", "Skift", "Flexibelt"] },
      { name: "extent", label: "Omfattning", type: "select", options: ["Heltid", "Deltid", "Både och"] },
      { name: "commute", label: "Möjlighet till pendling eller flytt", type: "select", options: ["Ja, kan pendla", "Ja, kan flytta", "Både pendla och flytta", "Nej"] },
    ],
  },
];

export default function Riktning() {
  const [reloadToken, setReloadToken] = useState(0);
  const TAG_FIELDS = ["job_types", "industries", "roles"];

  const applyDirection = async (p) => {
    const field = p.field;
    if (!field) return;
    const cur = (await api.get("/profile")).data.direction || {};
    const next = { ...cur };
    if (TAG_FIELDS.includes(field) || p.mode === "append") {
      const arr = Array.isArray(next[field]) ? [...next[field]] : (next[field] ? [next[field]] : []);
      const vals = Array.isArray(p.value) ? p.value : [p.value];
      vals.forEach((v) => { if (v && !arr.map((x) => String(x).toLowerCase()).includes(String(v).toLowerCase())) arr.push(v); });
      next[field] = arr;
    } else {
      next[field] = p.value;
    }
    await api.put("/profile/direction", { data: next });
    toast.success("Tillagt i din framtidsriktning");
    setReloadToken((t) => t + 1);
  };

  return (
    <div>
      <PageHeader title="Framtidsriktning" description="Berätta vart du är på väg. Detta hjälper både matchningen idag och framtida vägledning." />
      <div className="flex justify-end -mt-2 mb-4">
        <AiAssistant
          triggerLabel="Fråga AI om hjälp"
          triggerTestId="direction-ai-trigger"
          title="AI-hjälp med din framtidsriktning"
          intro="Jag hjälper dig sätta ord på vart du vill – roller, branscher och mål. Du godkänner varje tillägg."
          suggestions={[
            "Hjälp mig hitta roller som passar min bakgrund",
            "Vilka branscher kan passa mina intressen?",
            "Formulera ett framtidsmål utifrån min profil",
          ]}
          loadHistory={async () => (await api.get("/assistant/profile/direction")).data}
          send={async (message) => (await api.post("/assistant/profile", { section: "direction", message })).data}
          onApply={applyDirection}
        />
      </div>
      <ProfileForm groups={GROUPS} endpoint="/profile/direction" loadKey="direction" reloadToken={reloadToken} />

      <MinRiktning />
    </div>
  );
}

function GuidanceList({ icon: Icon, title, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="rounded-2xl border-border p-6">
      <div className="flex items-center gap-2 text-primary font-head font-semibold mb-3"><Icon className="h-5 w-5" /> {title}</div>
      <div className="space-y-2.5">{items.map(render)}</div>
    </Card>
  );
}

function ProfessionCard({ p, i }) {
  return (
    <div key={i} data-testid={`guidance-profession-${i}`} className="rounded-xl border border-border p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-sm">{p.title}</div>
        {typeof p.jobs_total === "number" && p.jobs_total > 0 && (
          <Badge variant="secondary" className="rounded-full shrink-0">{p.jobs_total.toLocaleString("sv-SE")} jobb</Badge>
        )}
      </div>
      <div className="text-sm text-muted-foreground mt-0.5">{p.why}</div>
      {(p.jobs || []).length > 0 && (
        <div className="mt-3 space-y-1.5">
          {p.jobs.slice(0, 3).map((j) => (
            <a key={j.id} href={j.url || "#"} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-between gap-2 text-sm rounded-lg bg-muted/50 px-3 py-2 hover:bg-muted transition-colors">
              <span className="min-w-0 truncate">{j.title} <span className="text-muted-foreground">· {j.employer || j.location}</span></span>
              <ExternalLink className="h-3.5 w-3.5 text-primary shrink-0" />
            </a>
          ))}
        </div>
      )}
      {p.jobs_url && (
        <a href={p.jobs_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-2 hover:underline" data-testid={`guidance-profession-jobslink-${i}`}>
          Se alla jobb på Platsbanken <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

function StudyTrackCard({ s, i }) {
  return (
    <div key={i} data-testid={`guidance-study-${i}`} className="rounded-xl border border-border p-4">
      <div className="font-medium text-sm">{s.title}</div>
      <div className="text-sm text-muted-foreground mt-0.5">{s.why}</div>
      {(s.educations || []).length > 0 && (
        <div className="mt-3 space-y-1.5">
          {s.educations.slice(0, 3).map((e, j) => (
            <a key={j} href={e.url || "#"} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-between gap-2 text-sm rounded-lg bg-muted/50 px-3 py-2 hover:bg-muted transition-colors">
              <span className="min-w-0 truncate">{e.title} <span className="text-muted-foreground">· YH{e.provider ? ` · ${e.provider}` : ""}</span></span>
              <ExternalLink className="h-3.5 w-3.5 text-primary shrink-0" />
            </a>
          ))}
        </div>
      )}
      {s.university_url && (
        <a href={s.university_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-2 hover:underline" data-testid={`guidance-study-unilink-${i}`}>
          Sök högskola/universitet på antagning.se <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

function MinRiktning() {
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState("full");
  const [needsInterests, setNeedsInterests] = useState(null);

  useEffect(() => {
    api.get("/guidance").then((r) => { setGuidance(r.data.guidance); if (r.data.mode) setMode(r.data.mode); setLoaded(true); });
  }, []);

  const generate = async (useMode) => {
    const m = useMode || mode;
    setLoading(true);
    setNeedsInterests(null);
    try {
      const { data } = await api.post(`/guidance?mode=${m}`);
      if (data.needs_interests) {
        setNeedsInterests(data.interest_examples || []);
        setGuidance(null);
      } else {
        setGuidance(data.guidance);
        toast.success("Vägledning uppdaterad");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const switchMode = (m) => { setMode(m); };

  return (
    <div className="mt-8">
      <Card className="rounded-2xl border-border p-6 md:p-8 bg-primary text-primary-foreground mb-5">
        <div className="flex items-center gap-2 font-head text-xl font-bold"><Compass className="h-5 w-5" /> Min riktning</div>
        <p className="text-sm text-primary-foreground/85 mt-2 max-w-2xl">
          AI-vägledning med förslag på framtida yrken och utbildningar utifrån dina intressen och din profil – kopplat till riktiga jobb och utbildningar. Alltid som förslag, aldrig som facit.
        </p>

        {/* Lägesväljare */}
        <div className="mt-5">
          <div className="text-xs uppercase tracking-wide text-primary-foreground/70 mb-2">Vad ska förslagen baseras på?</div>
          <div className="inline-flex rounded-full bg-white/15 p-1" data-testid="guidance-mode-toggle">
            <button
              onClick={() => switchMode("full")}
              data-testid="guidance-mode-full"
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors min-h-[40px] ${mode === "full" ? "bg-white text-primary" : "text-white hover:bg-white/10"}`}
            >
              <User className="h-4 w-4" /> Hela min profil
            </button>
            <button
              onClick={() => switchMode("interests")}
              data-testid="guidance-mode-interests"
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors min-h-[40px] ${mode === "interests" ? "bg-white text-primary" : "text-white hover:bg-white/10"}`}
            >
              <Heart className="h-4 w-4" /> Endast mina intressen
            </button>
          </div>
          <p className="text-sm text-primary-foreground/75 mt-2">
            {mode === "full"
              ? "Utgår från din erfarenhet, kompetens, intressen och riktning."
              : "Frikopplar erfarenhet och kompetens – förslagen baseras enbart på dina intressen, drivkrafter och vem du är. Perfekt för att utforska nytt."}
          </p>
        </div>

        <Button data-testid="generate-guidance-btn" onClick={() => generate()} disabled={loading}
          className="rounded-full bg-white text-primary hover:bg-white/90 mt-5">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {guidance ? "Uppdatera vägledning" : "Generera vägledning"}
        </Button>
      </Card>

      {needsInterests && (
        <Card className="rounded-2xl border-amber-300 bg-amber-50 p-6 mb-5" data-testid="guidance-needs-interests">
          <div className="flex items-center gap-2 font-head font-semibold text-amber-900"><Lightbulb className="h-5 w-5 text-amber-600" /> Berätta om dina intressen först</div>
          <p className="text-sm text-amber-900 mt-2">
            För att ge vägledning baserad på intressen behöver du fylla i vad du tycker om och drömmer om. Ett intresse kan till exempel vara:
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {needsInterests.map((ex) => (
              <span key={ex} className="rounded-full bg-white border border-amber-200 px-3 py-1.5 text-sm text-amber-900">{ex}</span>
            ))}
          </div>
          <Link to="/app/intressen">
            <Button className="rounded-full mt-4" data-testid="guidance-fill-interests-btn">Fyll i mina intressen <ExternalLink className="h-4 w-4 ml-2" /></Button>
          </Link>
        </Card>
      )}

      {loaded && !guidance && !needsInterests && (
        <p className="text-sm text-muted-foreground">Välj läge ovan och klicka på "Generera vägledning" för förslag.</p>
      )}

      {guidance && (
        <div className="grid gap-5 md:grid-cols-2 stagger-in">
          {guidance.summary && (
            <Card className="rounded-2xl border-border p-6 md:col-span-2 bg-muted/40">
              <p className="text-sm leading-relaxed">{guidance.summary}</p>
              <Badge variant="secondary" className="rounded-full mt-3">
                {mode === "interests" ? "Baserat på dina intressen · vägledningsunderlag" : "Vägledningsunderlag – inte ett facit"}
              </Badge>
            </Card>
          )}
          <Card className="rounded-2xl border-border p-6" data-testid="guidance-professions">
            <div className="flex items-center gap-2 text-primary font-head font-semibold mb-3"><Briefcase className="h-5 w-5" /> Yrkesförslag & riktiga jobb</div>
            <div className="space-y-3">{(guidance.profession_suggestions || []).map((p, i) => <ProfessionCard key={i} p={p} i={i} />)}</div>
          </Card>
          <Card className="rounded-2xl border-border p-6" data-testid="guidance-studytracks">
            <div className="flex items-center gap-2 text-primary font-head font-semibold mb-3"><GraduationCap className="h-5 w-5" /> Utbildningar att söka</div>
            <div className="space-y-3">{(guidance.study_tracks || []).map((s, i) => <StudyTrackCard key={i} s={s} i={i} />)}</div>
          </Card>
          <GuidanceList icon={Target} title="Kompetensluckor" items={guidance.competence_gaps}
            render={(g, i) => <div key={i} className="text-sm">• {g}</div>} />
          <GuidanceList icon={TrendingUp} title="Utvecklingsområden" items={guidance.development_areas}
            render={(g, i) => <div key={i} className="text-sm">• {g}</div>} />
          {guidance.career_paths && guidance.career_paths.length > 0 && (
            <Card className="rounded-2xl border-border p-6 md:col-span-2">
              <div className="flex items-center gap-2 text-primary font-head font-semibold mb-3"><Route className="h-5 w-5" /> Möjliga karriärvägar</div>
              <div className="grid gap-4 md:grid-cols-2">
                {guidance.career_paths.map((cp, i) => (
                  <div key={i} className="rounded-xl bg-muted/50 p-4">
                    <div className="font-medium text-sm mb-2">{cp.path}</div>
                    <ol className="list-decimal ml-5 space-y-1 text-sm text-muted-foreground">
                      {(cp.steps || []).map((s, j) => <li key={j}>{s}</li>)}
                    </ol>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
